import { Link } from "react-router-dom";
import { ArrowLeft, Compass, ShieldQuestion, FileText } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { runTour, dashboardTourSteps } from "@/lib/tours";

const FAQ_ITEMS = [
  { q: "Comment créer une nouvelle analyse HFACS ?", a: "Depuis le tableau de bord, cliquez sur \"Nouvelle analyse\", renseignez le site et le type d'événement, puis commencez par consigner les faits dans l'onglet Chronologie." },
  { q: "Comment importer une ancienne analyse Excel/CSV ?", a: "Depuis le tableau de bord, cliquez sur \"Importer une analyse\". L'IA extrait automatiquement les faits et facteurs HFACS du fichier, que vous pourrez ensuite vérifier et compléter." },
  { q: "Qu'est-ce que le Copilote IA ?", a: "Le Copilote IA analyse les faits saisis et suggère des facteurs HFACS potentiellement manquants, avec justification. Cette fonctionnalité est réservée à l'Administrateur national." },
  { q: "Comment fonctionne le diagramme causal ?", a: "Cliquez sur un facteur puis sur un second facteur (ou l'incident) pour créer un lien causal entre eux. La chaîne causale la plus longue/validée est mise en évidence en rouge." },
  { q: "Qu'est-ce qu'un chemin critique ?", a: "Un chemin critique est un enchaînement de facteurs causaux jugé déterminant dans la survenue de l'incident. Il peut être proposé, vérifié, validé ou rejeté par un Directeur." },
  { q: "Comment fonctionne le plan HFIX ?", a: "Pour chaque chemin critique validé, définissez une ou plusieurs actions correctives, notées sur 25 (faisabilité x efficacité), avec un statut de suivi (À faire, En cours, Réalisée, Vérifiée efficace)." },
  { q: "À quoi sert le Mode Réunion ?", a: "Il projette l'analyse en plein écran (chronologie, facteurs, diagramme causal, chemins critiques) pour l'animer facilement devant un comité ou en réunion de sécurité." },
  { q: "Comment générer le rapport PDF ?", a: "Depuis l'onglet Vue d'ensemble d'une analyse, cliquez sur \"Générer le rapport PDF\". Il sera automatiquement déposé dans le dossier Google Drive de l'analyse." },
];

export default function Aide() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="aide-page">
      <AppHeader />
      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" /> Retour au tableau de bord
        </Link>

        <h1 className="font-heading font-black uppercase tracking-tight text-3xl mb-2">Aide &amp; tutoriel</h1>
        <p className="text-sm text-muted-foreground mb-10">Retrouvez ici les visites guidées et les réponses aux questions fréquentes sur HFACS.</p>

        <div className="grid sm:grid-cols-2 gap-4 mb-12">
          <div className="bg-card border border-border rounded-md p-5">
            <Compass className="h-5 w-5 text-primary mb-3" />
            <h2 className="font-heading font-semibold text-sm uppercase mb-2">Visite guidée du tableau de bord</h2>
            <p className="text-sm text-muted-foreground mb-4">Redécouvrez les boutons clés du tableau de bord (nouvelle analyse, import, liste des analyses).</p>
            <Link to="/dashboard">
              <Button data-testid="aide-replay-dashboard-tour" onClick={() => setTimeout(() => runTour("dashboard", dashboardTourSteps, { force: true }), 400)} className="bg-primary hover:bg-primary/90 text-primary-foreground">
                Revoir cette visite
              </Button>
            </Link>
          </div>
          <div className="bg-card border border-border rounded-md p-5">
            <ShieldQuestion className="h-5 w-5 text-accent mb-3" />
            <h2 className="font-heading font-semibold text-sm uppercase mb-2">Visite guidée d'une analyse</h2>
            <p className="text-sm text-muted-foreground mb-4">Ouvrez n'importe quelle analyse puis cliquez sur l'icône d'aide (?) en haut à droite pour relancer sa visite guidée.</p>
            <Link to="/dashboard" data-testid="aide-goto-analysis-link" className="text-sm text-accent hover:underline">Aller au tableau de bord →</Link>
          </div>
        </div>

        <h2 className="font-heading uppercase font-semibold text-sm text-muted-foreground mb-3">Questions fréquentes</h2>
        <div className="space-y-3 mb-12">
          {FAQ_ITEMS.map((item, i) => (
            <details key={i} data-testid={`aide-faq-item-${i}`} className="bg-card border border-border rounded-md p-4 group">
              <summary className="font-medium text-sm cursor-pointer">{item.q}</summary>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{item.a}</p>
            </details>
          ))}
        </div>

        <Link to="/politique-confidentialite" data-testid="aide-privacy-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <FileText className="h-4 w-4" /> Politique de confidentialité (RGPD)
        </Link>
      </main>
    </div>
  );
}
